<template>
  <el-card v-loading="loading" shadow="never">
    <div slot="header">
      <el-breadcrumb separator-class="el-icon-arrow-right">
        <el-breadcrumb-item :to="{ name: $root.getDashboardPath() }">Dashboard</el-breadcrumb-item>
        <el-breadcrumb-item v-if="$root.state.facility" :to="{ name: 'Facility Users' }">{{String($root.state.facilityData.code).toUpperCase()}} Users</el-breadcrumb-item>
        <el-breadcrumb-item v-else :to="{ name: 'Users' }">Users</el-breadcrumb-item>
        <el-breadcrumb-item :to="{ name: 'User Form' }">Edit User</el-breadcrumb-item>
      </el-breadcrumb>
    </div>
    <el-tabs v-model="activeTab">
      <el-tab-pane label="Account" name="account">
        <el-form v-model="password" inline>
          <el-form-item label="Password" prop="value">
            <el-input v-model="password.value" type="password">
              <el-button slot="prepend" @click="generateRandomCode">
                Generate random password
              </el-button>
              <el-button :disabled="!password.value" slot="append" @click="copyText" icon="el-icon-copy-document"></el-button>
            </el-input>
          </el-form-item>
          <el-form-item label="Temporary" prop="temporary">
            <el-checkbox v-model="password.temporary"></el-checkbox>
          </el-form-item>
          <el-button :disabled="!password.value" @click="resetPassword">
            Reset Password
          </el-button>
        </el-form>
        <el-form>
          <el-form-item label="Title" prop="title">
           <el-input v-model="title" ></el-input>
          </el-form-item>
          <el-form-item label="Firstname" prop="firstName">
           <el-input v-model="firstName" ></el-input>
          </el-form-item>
          <el-form-item label="Lastname" prop="lastName">
           <el-input v-model="lastName" ></el-input>
          </el-form-item>
          <el-form-item label="Salesforce ID" prop="salesforceId">
           <el-input v-model="salesforceId" ></el-input>
          </el-form-item>
          <el-form-item label="Profile Image" prop="profile">
            <Upload
              listType="picture"
              @upload-completed="setProfile"
              @remove-upload="clearProfile"
            />
            <el-input v-model="profile" placeholder="Profile image URL"></el-input>
          </el-form-item>
          <el-form-item label="Phone" prop="phone">
            <el-input v-model="phone" :disabled="!isPhoneEditable"></el-input>
          </el-form-item>
          <el-form-item label="Location" prop="department">
            <el-select v-model="department" placeholder="Select location" filterable style="width: 100%;" :disabled="!canEditLocation">
              <el-option
                v-for="option in locationOptions"
                :key="option.value || option.label"
                :label="option.label"
                :value="option.value"
              />
            </el-select>
          </el-form-item>
          <el-button @click="saveEditedManagerDetails">Save</el-button>
          <el-divider></el-divider>
          <div style="margin-top:12px;">
            <div style="font-weight:bold;margin-bottom:8px;">Signature Preview</div>
            <SignaturePreview
              ref="editUserSignaturePreview"
              :profile="profile"
              :name="signatureName"
              :email="manager && manager.email"
              :phone="phone"
              :title="title"
              :department="department"
              :address="signatureAddress"
              :logo-url="logoUrl"
            />
          </div>
        </el-form>
      </el-tab-pane>
      <el-tab-pane label="Access" name="access">
        <el-form v-model="role" inline>
          <el-form-item
            label="Role"
            prop="role"
          >
            <el-radio-group v-model="role.selectedRole" @change="role.showRoleSave = true">
              <el-tooltip
                :key="realmRole.id" v-for="realmRole in role.realmRoles"
                placement="top-start"
                trigger="hover"
                :content="realmRole.description">
                <el-radio-button :label="realmRole.name.toUpperCase()" border></el-radio-button>
              </el-tooltip>
            </el-radio-group>
          </el-form-item>
          <el-button v-if="role.showRoleSave" @click="setManagerRole">
            Save
          </el-button>
        </el-form>
        <el-form inline>
          <el-form-item label="Location Access">
            <el-select
              v-model="selectedLocations"
              @change="showLocationSave = true"
              multiple
              placeholder="Select"
              :disabled="!canEditLocation"
            >
              <el-option
                v-for="item in locations"
                :key="item._id"
                :label="item.title"
                :value="item._id">
              </el-option>
            </el-select>
          </el-form-item>
          <el-button v-if="showLocationSave" @click="saveLocations">
            Save
          </el-button>
        </el-form>
      </el-tab-pane>
    </el-tabs>
  </el-card>
</template>

<script>
import axios from 'axios';
import randomCode from '../utils/random';
import { getRealmRoles, getLocations, getLocationsForPortal } from '../utils/commonApi';
import { resetManagerTemplate } from '../utils/emailTemplates';
import { AUTH_API_BASE, EMAIL_API_BASE } from '../env';
import Upload from './Uploader';
import SignaturePreview from './SignaturePreview';

export default {
  name: 'EditUser',
  components: {
    Upload,
    SignaturePreview,
  },
  props: { manager: Object, default: null },
  watch: {
    manager(n) {
      this.role.selectedRole = n.role ? n.role.name.toUpperCase() : '';
      this.selectedLocations = n.attributes ? n.attributes.locations : [];
      this.firstName = n.firstName ? n.firstName : '';
      this.lastName = n.lastName ? n.lastName : '';
      this.title = n.attributes && n.attributes.title ? n.attributes.title[0] : '';
      this.salesforceId = this.manager.attributes.salesforceId;
      this.profile = n.attributes && n.attributes.profile ? n.attributes.profile[0] : null;
      this.phone = n.attributes && n.attributes.phone ? n.attributes.phone[0] : null;
      const department = n.attributes && n.attributes.department ? n.attributes.department[0] : null;
      this.department = department === 'None' ? '' : department;
      if (!this.department) {
        this.manualPhone = this.phone;
      }
      this.resolveDepartmentTitle();
      this.syncPhoneWithLocation();
    },
    department: {
      immediate: true,
      handler(newValue, oldValue) {
        if (!newValue) {
          if (this.manualPhone !== null && this.manualPhone !== undefined) {
            this.phone = this.manualPhone;
          }
          return;
        }
        if (!oldValue) {
          this.manualPhone = this.phone;
        }
        this.syncPhoneWithLocation();
      },
    },
    phone(newValue) {
      if (!this.department) {
        this.manualPhone = newValue;
      }
    },
  },
  data() {
    return {
      activeTab: 'account',
      loading: false,
      logoUrl: 'https://email-attachments-public.s3.us-east-2.amazonaws.com/rs.png',
      password: {
        value: '',
        temporary: true,
      },
      role: {
        realmRoles: [],
        selectedRole: null,
        showRoleSave: false,
      },
      locations: [],
      selectedLocations: [],
      title: '',
      firstName: '',
      lastName: '',
      salesforceId: null,
      profile: null,
      phone: null,
      department: null,
      showLocationSave: false,
      portalLocations: [],
      manualPhone: null,
    };
  },
  computed: {
    canEditLocation() {
      const roles = (this.$keycloak && this.$keycloak.realmAccess && this.$keycloak.realmAccess.roles) || [];
      return roles.includes('level_1') || roles.includes('level_2');
    },
    isPhoneEditable() {
      return !this.department;
    },
    signatureName() {
      const name = [this.firstName, this.lastName].filter(Boolean).join(' ').trim();
      if (name) {
        return name;
      }
      if (this.manager) {
        const managerName = [this.manager.firstName, this.manager.lastName].filter(Boolean).join(' ').trim();
        if (managerName) {
          return managerName;
        }
        if (this.manager.username) {
          return this.manager.username;
        }
        if (this.manager.email) {
          return this.manager.email;
        }
      }
      return 'Signature';
    },
    signatureAddress() {
      if (!this.department) {
        return '';
      }
      const match = (this.portalLocations || []).find((facility) => this.isLocationMatch(facility, this.department));
      if (!match) {
        return '';
      }
      return this.getLocationAddress(match);
    },
    locationOptions() {
      const options = [];
      const seen = new Set();

      if (Array.isArray(this.portalLocations) && this.portalLocations.length) {
        this.portalLocations.forEach((facility) => {
          const value = this.getLocationValue(facility);
          if (!value || seen.has(value)) {
            return;
          }
          const label = this.getLocationLabel(facility, value);
          seen.add(value);
          options.push({ label, value });
        });
      } else {
        const rawLocations = (this.manager && this.manager.attributes && this.manager.attributes.locations) || [];
        const locationsList = Array.isArray(rawLocations) ? rawLocations : [rawLocations];
        locationsList
          .filter(Boolean)
          .map((code) => String(code).trim())
          .filter(Boolean)
          .forEach((code) => {
            if (!seen.has(code)) {
              seen.add(code);
              options.push({ label: code, value: code });
            }
          });
      }

      if (this.department && !seen.has(this.department)) {
        options.unshift({ label: this.department, value: this.department });
      }

      return [
        { label: 'None', value: '' },
        ...options,
      ];
    },
  },
  async mounted() {
    this.loading = true;
    this.role.realmRoles = await getRealmRoles(this.$keycloak.token);

    this.role.selectedRole = this.manager.role.name.toUpperCase();

    this.selectedLocations = this.manager.attributes ? this.manager.attributes.locations : [];
    this.title = this.manager.attributes && this.manager.attributes.title ? this.manager.attributes.title[0] : '';
    this.firstName = this.manager.firstName ? this.manager.firstName : '';
    this.lastName = this.manager.lastName ? this.manager.lastName : '';
    this.locations = await getLocations(this.$keycloak.token);
    this.salesforceId = this.manager.attributes ? this.manager.attributes.salesforceId : null;
    this.profile = this.manager.attributes && this.manager.attributes.profile ? this.manager.attributes.profile[0] : null;
    this.phone = this.manager.attributes && this.manager.attributes.phone ? this.manager.attributes.phone[0] : null;
    const department = this.manager.attributes && this.manager.attributes.department ? this.manager.attributes.department[0] : null;
    this.department = department === 'None' ? '' : department;
    if (!this.department) {
      this.manualPhone = this.phone;
    }
    await this.loadLocationsForPortal();
    this.loading = false;
  },
  methods: {
    getLocationValue(facility) {
      if (!facility || typeof facility !== 'object') {
        return '';
      }
      return facility.title || facility.name || facility.code || facility.locationCode || facility._id || '';
    },
    isLocationMatch(facility, value) {
      if (!facility || typeof facility !== 'object' || !value) {
        return false;
      }
      const candidates = [
        facility.title,
        facility.name,
        facility.code,
        facility.locationCode,
        facility._id,
      ];
      return candidates.some((candidate) => candidate && String(candidate) === String(value));
    },
    getLocationLabel(facility, fallbackValue) {
      if (!facility || typeof facility !== 'object') {
        return fallbackValue || '';
      }
      return facility.title || facility.name || facility.code || facility._id || fallbackValue || '';
    },
    getLocationPhone(facility) {
      if (!facility || typeof facility !== 'object') {
        return '';
      }
      return this.normalizeValue(
        (facility.manager && facility.manager.phone)
        || facility.phone
        || (facility.contact && facility.contact.phone)
        || facility.contactPhone
        || '',
      );
    },
    getLocationAddress(facility) {
      if (!facility || typeof facility !== 'object') {
        return '';
      }
      const addressInfo = facility.addressInfo || {};
      const addressLine1 = addressInfo.addressLine1 || facility.addressLine1 || facility.address || '';
      const addressLine2 = addressInfo.addressLine2 || facility.addressLine2 || facility.address2 || '';
      const city = addressInfo.city || facility.city || '';
      const state = addressInfo.state || facility.state || '';
      const pinCode = addressInfo.pinCode || facility.pinCode || facility.postalCode || '';
      return [addressLine1, addressLine2, city, state, pinCode].filter(Boolean).join(', ');
    },
    async loadLocationsForPortal() {
      try {
        const locations = await getLocationsForPortal(this.$keycloak.token);
        this.portalLocations = Array.isArray(locations) ? locations : [];
        this.resolveDepartmentTitle();
        this.syncPhoneWithLocation();
      } catch (err) {
        this.portalLocations = [];
      }
    },
    resolveDepartmentTitle() {
      if (!this.department || !Array.isArray(this.portalLocations) || !this.portalLocations.length) {
        return;
      }
      const match = this.portalLocations.find((facility) => this.isLocationMatch(facility, this.department));
      if (match && match.title && this.department !== match.title) {
        this.department = match.title;
      }
    },
    syncPhoneWithLocation() {
      if (!this.department) {
        return;
      }
      const match = (this.portalLocations || []).find((facility) => this.isLocationMatch(facility, this.department));
      if (!match) {
        return;
      }
      const locationPhone = this.getLocationPhone(match);
      if (locationPhone) {
        this.phone = locationPhone;
      }
    },
    normalizeValue(value) {
      if (Array.isArray(value)) {
        return value[0] || '';
      }
      return value || '';
    },
    setProfile(url) {
      this.profile = url;
    },
    clearProfile() {
      this.profile = null;
    },
    generateRandomCode() {
      const code = randomCode();
      this.password.value = code;
    },
    async saveLocations() {
      try {
        this.loading = true;
        await axios.post(`${AUTH_API_BASE}/manager/${this.manager.id}`, {
          email: this.manager.email,
          username: this.manager.username,
          firstName: this.firstName,
          lastName: this.lastName,
          attributes: {
            ...this.manager.attributes,
            locations: this.selectedLocations,
          },
        },
        {
          headers: {
            Authorization: `Bearer ${this.$keycloak.token}`,
          },
        });

        this.$message.success({
          title: 'Success',
          message: 'Locations Updated successfully',
        });
        this.loading = false;
        this.$emit('update');
      } catch (err) {
        this.loading = false;
        this.$message.error({
          title: 'Error',
          message: err.response.data,
        });
      }
    },
    async saveEditedManagerDetails() {
      try {
        this.loading = true;
        await axios.post(`${AUTH_API_BASE}/manager/${this.manager.id}`, {
          email: this.manager.email,
          username: this.manager.username,
          firstName: this.firstName,
          lastName: this.lastName,
          attributes: {
            ...this.manager.attributes,
            salesforceId: this.salesforceId,
            title: this.title,
            profile: this.profile,
            phone: this.phone,
            department: this.department,
          },
        },
        {
          headers: {
            Authorization: `Bearer ${this.$keycloak.token}`,
          },
        });
        this.$message.success({
          title: 'Success',
          message: 'Updated successfully',
        });
        this.loading = false;
        this.$emit('update');
      } catch (err) {
        this.loading = false;
        this.$message.error({
          title: 'Error',
          message: err.response.data,
        });
      }
    },
    async copyText() {
      try {
        await navigator.clipboard.writeText(this.password.value);
        this.$message.success({
          title: 'Copied',
          message: 'Password copied to clipboard',
        });
      } catch (err) {
        this.$message.error({
          title: 'Error',
          message: err.message,
        });
      }
    },
    async resetPassword() {
      try {
        this.loading = true;
        const response = await axios.put(`${AUTH_API_BASE}/manager/${this.manager.id}/reset-password`, {
          ...this.password,
          type: 'password',
        },
        {
          headers: {
            Authorization: `Bearer ${this.$keycloak.token}`,
          },
        });

        try {
          await this.sendEmail();
        } catch (err) {
          this.$message.error({
            title: 'Error',
            message: err.response.data,
          });
        }

        this.loading = false;
        this.$message.success({
          title: 'Success',
          message: response.data,
        });
      } catch (err) {
        this.loading = false;
        this.$message.error({
          title: 'Error',
          message: err.response.data,
        });
      }
    },
    async sendEmail() {
      try {
        const mjmlTemplate = resetManagerTemplate(
          this.manager.firstName, this.manager.email, this.password.value,
        );
        await axios.post(`${EMAIL_API_BASE}/send/email`, {
          from: this.$keycloak.tokenParsed.email,
          to: this.manager.email,
          subject: 'Reset password.',
          mjml: true,
          body: mjmlTemplate,
        }, {
          headers: {
            Authorization: `Bearer ${this.$keycloak.token}`,
          },
        });
      } catch (err) {
        this.$message.error({
          title: 'Error',
          message: 'Send email failed',
        });
      }
    },
    async setManagerRole() {
      this.loading = true;
      const [selectedFromRealmRole] = this.role.realmRoles.filter((role) => {
        if (role.name === this.role.selectedRole.toLowerCase()) {
          return role;
        }
      });

      await axios.post(`${AUTH_API_BASE}/manager/role/${this.manager.id}`, {
        addRole: [{
          id: selectedFromRealmRole.id,
          name: selectedFromRealmRole.name,
        }],
        removeRole: this.manager.role ? [
          {
            id: this.manager.role.id,
            name: this.manager.role.name,
          }] : [],
      }, {
        headers: {
          Authorization: `Bearer ${this.$keycloak.token}`,
        },
      });
      this.loading = false;
      this.role.showRoleSave = false;
      this.$message.success({
        title: 'Success',
        message: 'Role updated successfully',
      });
      this.$emit('update');
    },
  },
};
</script>



// WEBPACK FOOTER //
// src/components/EditUser.vue